// ── PwaInstallPrompt — 홈화면 추가 안내 ───────────────────────
// Android Chrome: beforeinstallprompt 이벤트 캡처해서 설치 트리거 버튼
// iOS Safari: 자동 설치 불가 → "공유 → 홈 화면에 추가" 안내 카드
// 사용자가 닫으면 7일간 다시 안 띄움
import React, { useEffect, useState } from 'react';
import { useLang } from '../i18n/LangContext';   // V147 중국어 화면

const DISMISS_KEY = 'semfit_pwa_dismissed_until';
const DISMISS_DAYS = 7;

const isStandalone = () =>
  (typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
     window.navigator.standalone === true));

const isIos = () =>
  typeof window !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent || '') &&
  !/crios|fxios|edgios/i.test(navigator.userAgent || '');

const isMobile = () =>
  typeof window !== 'undefined' &&
  /android|iphone|ipad|mobile/i.test(navigator.userAgent || '');

// V147: 셈핏 안드로이드 앱(이미 앱)·위챗 안 브라우저(홈 화면 추가 불가)에서는 안내하지 않는다
const isInAppShell = () =>
  typeof navigator !== 'undefined' && /SemfitApp\/|MicroMessenger/i.test(navigator.userAgent || '');

const isDismissed = () => {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until > Date.now();
  } catch { return false; }
};

export default function PwaInstallPrompt() {
  const { lang } = useLang();
  const L = (ko, zh) => (lang === 'zh' ? zh : ko);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;          // 이미 설치됨
    if (isInAppShell()) return;          // V147 셈핏 앱·위챗 안
    if (isDismissed()) return;           // 사용자가 닫음
    if (!isMobile()) return;             // 모바일에서만 노출

    // Android Chrome: beforeinstallprompt 캡처
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // 첫 방문 후 3초 후 표시 (UX 부담 줄임)
      setTimeout(() => setShow(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — 자동 프롬프트 없음. 5초 후 안내 카드.
    if (isIos()) {
      const t = setTimeout(() => setShowIosGuide(true), 5000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShow(false);
      } else {
        dismiss();
      }
    } catch (e) {
      console.warn('PWA install prompt failed:', e);
      dismiss();
    } finally {
      setDeferredPrompt(null);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    } catch {}
    setShow(false);
    setShowIosGuide(false);
  };

  // Android Chrome 카드
  if (show && deferredPrompt) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={iconStyle}>📱</div>
          <div style={{ flex: 1 }}>
            <div style={titleStyle}>{L('홈 화면에 셈핏 추가', '把 SEMFIT 添加到主屏幕')}</div>
            <div style={subtitleStyle}>{L('한 번 누르면 앱처럼 빠르게 열려요', '以后点一下就能像 App 一样打开')}</div>
          </div>
          <button onClick={dismiss} style={closeBtnStyle}>✕</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={dismiss} style={secondaryBtnStyle}>{L('나중에', '以后再说')}</button>
          <button onClick={handleInstall} style={primaryBtnStyle}>
            {L('홈 추가하기', '添加')}
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari 안내 카드
  if (showIosGuide) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={iconStyle}>📱</div>
          <div style={{ flex: 1 }}>
            <div style={titleStyle}>{L('홈 화면에 셈핏 추가', '把 SEMFIT 添加到主屏幕')}</div>
            <div style={subtitleStyle}>
              {lang === 'zh'
                ? <>点 Safari 底部的 <strong>分享按钮 ⬆️</strong> → <strong>「添加到主屏幕」</strong></>
                : <>Safari 하단 <strong>공유 버튼 ⬆️</strong> → <strong>"홈 화면에 추가"</strong></>}
            </div>
          </div>
          <button onClick={dismiss} style={closeBtnStyle}>✕</button>
        </div>
        <div style={{ marginTop: 8 }}>
          <button onClick={dismiss} style={{ ...secondaryBtnStyle, width: '100%' }}>
            {L('알겠어요', '知道了')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}

// ── Styles ─────────────────────────────────────────────────
const cardStyle = {
  position: 'fixed',
  bottom: 70,  // 모바일 하단 탭(56px) 위
  left: 12, right: 12,
  background: '#fff',
  borderRadius: 14,
  padding: '14px 16px',
  boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
  border: '1.5px solid #FF6F0F',
  zIndex: 250,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  animation: 'slideUp 0.3s ease-out',
};
const iconStyle = {
  fontSize: 32, lineHeight: 1, flexShrink: 0,
};
const titleStyle = {
  fontSize: 14, fontWeight: 800, color: '#18181B', marginBottom: 2,
};
const subtitleStyle = {
  fontSize: 12, color: '#6b7280', lineHeight: 1.4,
};
const closeBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 16, color: '#9ca3af', padding: '4px 8px',
};
const primaryBtnStyle = {
  flex: 1, padding: '9px 14px', borderRadius: 8, border: 'none',
  background: '#FF6F0F', color: '#fff', fontSize: 13, fontWeight: 800,
  cursor: 'pointer',
};
const secondaryBtnStyle = {
  flex: 1, padding: '9px 14px', borderRadius: 8,
  border: '1.5px solid #e5e7eb', background: '#fff',
  color: '#6b7280', fontSize: 13, fontWeight: 700,
  cursor: 'pointer',
};
